/**
 * Centralised registry of localStorage keys used by RheumHub.
 *
 * Any per-user state stored in `localStorage` MUST start with the `rheumhub_`
 * prefix so it is automatically wiped on sign-out / Clerk user change.
 *
 * Examples of keys covered by the prefix sweep:
 *   - rheumhub_role
 *   - rheumhub_ai_consent_v1
 *   - rheumhub_clinical_safety_ack_v1
 *   - rheumhub_practice_level
 *   - rheumhub_storage_notice_dismissed
 *   - rheumhub_session_notice_v1
 *   - rheumhub_patient_recently_viewed
 *   - rheumhub_patient_next_appointment
 *   - rheumhub_patient_chat_privacy_notice_v1
 *   - rheumhub_monitoring_entries
 *   - rheumhub_nmc_revalidation_date
 *   - rheumhub_reflection_<moduleId>_s<sectionId>
 *   - rheumhub_pharmacist_revalidation
 *   - rheumhub_doctor_appraisal_date
 *   - rheumhub_doctor_revalidation_date
 *   - rheumhub_doctor_gmc_checklist
 *   - rheumhub_doctor_interview_notes
 *   - rheumhub_dismissed_promotions_<userId>
 */
export const RHEUMHUB_LS_PREFIX = "rheumhub_";

/**
 * Remove every `rheumhub_*` entry from `localStorage`.
 *
 * Call this whenever the active user changes (sign-out, Clerk user switch)
 * so that the next person using the same browser does not inherit checklist
 * progress, recently viewed items, appointments, monitoring entries, etc.
 */
export function clearRheumHubLocalStorage(): void {
  if (typeof window === "undefined") return;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(RHEUMHUB_LS_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage may throw in private mode or when quota is exceeded — swallow.
  }
}
